import { Refresh } from 'iconsax-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { AppRoutes } from '@/constants/appRoutes'
import { catchWithSentry } from '@/hooks/useCatchWithSentry'
import {
  EReportType,
  type IExpiredReportItem,
} from '@/store/api/Enterprise/Enterprise.types'
import {
  useLazyGetSubjectBasicInformationQuery,
  useMakeBackgroundCheckOpenedMutation,
} from '@/store/api/Person/Person.query'
import { EReportType as EPersonReportType } from '@/store/api/Person/Person.types'
import { selectWorkspaceCreditsFromStore } from '@/store/api/Users/Users.selector'
import { formatDate } from '@/utils/date'
import { weCheckPalette } from '@/utils/palette/weCheckPalette'

export const ExpiredSocialAnalysisItem = ({
  id,
  subjectFullName,
  originalCompletedAt,
  deletedAt,
  deletionReason,
  subjectInputs,
  reportType,
}: IExpiredReportItem) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [isLoadingFollowUpReport, setIsLoadingFollowUpReport] = useState(false)

  const [getSubjectBasicInformation] = useLazyGetSubjectBasicInformationQuery()
  const [makeBackgroundCheckOpened] = useMakeBackgroundCheckOpenedMutation()

  const workspaceCredits = useSelector(selectWorkspaceCreditsFromStore)
  const aiAnalystReportCredits = workspaceCredits?.aiAnalystReportCredits ?? 0
  const adverseMediaReportCredits =
    workspaceCredits?.adverseMediaReportCredits ?? 0
  const assistedReportCredits = workspaceCredits?.assistedReportCredits ?? 0
  const aiReportCredits = workspaceCredits?.aiReportCredits ?? 0

  const initials = subjectFullName
    ? subjectFullName
        .split(' ')
        .map((word) => word[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : ''

  const canFollowUp =
    reportType &&
    (reportType === EReportType.AiAnalyst ||
      reportType === EReportType.AdverseMedia ||
      reportType === EReportType.Assited ||
      reportType === EReportType.Ai)

  const onPressFollowUpReport = async () => {
    setIsLoadingFollowUpReport(true)

    try {
      const { info: reportData } = await getSubjectBasicInformation({
        id,
      }).unwrap()

      if (reportData) {
        if (
          reportData.reportType === EPersonReportType.AiAnalyst &&
          aiAnalystReportCredits <= 0
        ) {
          toast.error(t('feedback.toast.noCredits'))
          return
        } else if (
          reportData.reportType === EPersonReportType.AdverseMedia &&
          adverseMediaReportCredits <= 0
        ) {
          toast.error(t('feedback.toast.noCredits'))
          return
        } else if (
          reportData.reportType === EPersonReportType.Assisted &&
          assistedReportCredits <= 0
        ) {
          toast.error(t('feedback.toast.noCredits'))
          return
        } else if (
          reportData.reportType === EPersonReportType.Ai &&
          aiReportCredits <= 0
        ) {
          toast.error(t('feedback.toast.noCredits'))
          return
        } else if (reportData.reportType === EPersonReportType.FacialMatching) {
          toast.error(t('dashboard.reportsScreen.followUpReportNotAvailable'))
          return
        }

        try {
          await makeBackgroundCheckOpened({ id }).unwrap()
        } catch {
          /* ignore */
        }

        navigate(`${AppRoutes.newFollowUpReport}?reportId=${id}`)
      }
    } catch (e) {
      catchWithSentry(e)
      toast.error(t('feedback.toast.error'))
    } finally {
      setIsLoadingFollowUpReport(false)
    }
  }

  return (
    <div className="flex items-center w-full border-b border-neutralColor5 last:border-b-0 gap-2 min-h-[72px]">
      <div className="flex flex-1 min-w-0 items-center gap-3 px-2">
        <Avatar className="w-8 h-8 shrink-0">
          <AvatarImage />
          <AvatarFallback className="bg-baseColor text-neutralColor text-xs">
            {initials}
          </AvatarFallback>
        </Avatar>
        <p className="text-sm text-neutralColor truncate">{subjectFullName}</p>
      </div>

      <div className="hidden md:block flex-1 min-w-0 px-2">
        <p className="text-sm text-neutralColor truncate">
          {reportType ? getReportLabel(reportType, t) : ''}
        </p>
      </div>

      <div className="hidden lg:flex flex-col flex-1 min-w-0 px-2 gap-0.5">
        <p className="text-xs text-neutralColor">
          {t('dashboard.reportsScreen.expiredReports.originalDate')}
        </p>
        <p className="text-sm text-neutralColor">
          {formatDate(originalCompletedAt, 'MM/dd/yyyy')}
        </p>
      </div>

      <div className="hidden lg:flex flex-col flex-1 min-w-0 px-2 gap-0.5">
        <p className="text-xs text-neutralColor">
          {t('dashboard.reportsScreen.expiredReports.archivedDate')}
        </p>
        <p className="text-sm text-neutralColor">
          {formatDate(deletedAt, 'MM/dd/yyyy')}
        </p>
      </div>

      <div className="w-10 flex justify-end mr-2 shrink-0">
        {canFollowUp && (
          <button
            type="button"
            onClick={onPressFollowUpReport}
            disabled={isLoadingFollowUpReport}
            aria-label={t('dashboard.reportsScreen.followUpReport')}
            className="p-1.5 rounded-md hover:bg-baseColorHighlight transition-colors disabled:opacity-50"
          >
            <Refresh size={20} color={weCheckPalette.neutralColor} />
          </button>
        )}
      </div>
    </div>
  )
}

const getReportLabel = (
  reportType: EReportType,
  t: (key: string) => string,
): string => {
  switch (reportType) {
    case EReportType.AdverseMedia:
      return t('dashboard.reportsScreen.adverseMedia')
    case EReportType.Ai:
      return t('dashboard.reportsScreen.aIReport')
    case EReportType.AiAnalyst:
      return t('dashboard.reportsScreen.aIAnalystReport')
    case EReportType.Assited:
      return t('dashboard.reportsScreen.weCheckAssisted')
    default:
      return ''
  }
}
